import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Plus, FileText, Download, Trash, ArrowsClockwise,
  CheckCircle, XCircle, Clock, SpinnerGap, UploadSimple,
} from '@phosphor-icons/react';
import BackButton from '@/components/BackButton';
import { fmtKsa } from '@/utils/formatKsa';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

const STATUS_META = {
  pending:  { ar: 'قيد المراجعة', en: 'Pending',  cls: 'bg-amber-100 text-amber-800 border-amber-200',  Icon: Clock },
  approved: { ar: 'مقبول',         en: 'Approved', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', Icon: CheckCircle },
  rejected: { ar: 'مرفوض',         en: 'Rejected', cls: 'bg-red-100 text-red-800 border-red-200',     Icon: XCircle },
  cancelled_by_employee: { ar: 'ملغى من قبلي', en: 'Cancelled by you', cls: 'bg-slate-200 text-slate-700 border-slate-300', Icon: XCircle },
};

export default function EmployeeRequests() {
  const { language, isRTL } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAr = language === 'ar';
  const direction = isRTL ? 'rtl' : 'ltr';

  const [types, setTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // form state
  const [showForm, setShowForm] = useState(false);
  const [resubmitId, setResubmitId] = useState(null);
  const [type, setType] = useState('salary_certificate');
  const [purpose, setPurpose] = useState('');
  const [country, setCountry] = useState('');
  const [addressee, setAddressee] = useState('');
  // Employee-side attachment (required for id_card). Stores the URL
  // returned by the upload endpoint; a preview URL is kept locally.
  const [employeeAttachmentUrl, setEmployeeAttachmentUrl] = useState('');
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const isIdCard = type === 'id_card';

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [typesRes, mineRes] = await Promise.all([
        axios.get(`${API}/requests-catalog/types`, { withCredentials: true }),
        axios.get(`${API}/requests/my`, { withCredentials: true }),
      ]);
      setTypes(typesRes.data || []);
      setItems(mineRes.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-open the "Other request" form when arriving from the floating
  // HR widget (or any other deep-link that sets navigation state).
  useEffect(() => {
    const openNew = location.state?.openNew;
    if (!openNew) return;
    setResubmitId(null);
    setType(openNew);
    setPurpose(''); setCountry(''); setAddressee('');
    setEmployeeAttachmentUrl('');
    setShowForm(true);
    // Clear the state so refreshes / back-nav don't re-open the dialog.
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  // ---- Agency Contracted Workers: no service requests at all ----
  useEffect(() => {
    if (user?.is_contracted_worker) {
      navigate('/home', { replace: true });
    }
  }, [user, navigate]);
  if (user?.is_contracted_worker) {
    return null;
  }

  const openNew = () => {
    setResubmitId(null);
    setType('salary_certificate');
    setPurpose(''); setCountry(''); setAddressee('');
    setEmployeeAttachmentUrl('');
    setShowForm(true);
  };

  const openResubmit = (req) => {
    setResubmitId(req.id);
    setType(req.type);
    setPurpose(req.purpose || '');
    setCountry(req.country || '');
    setAddressee(req.addressee || '');
    setEmployeeAttachmentUrl(req.employee_attachment_url || '');
    setShowForm(true);
  };

  const handleUploadAttachment = async (file) => {
    if (!file) return;
    if (!/\.(png|jpg|jpeg)$/i.test(file.name)) {
      toast.error(isAr ? 'يرجى اختيار صورة (PNG أو JPG)' : 'Please choose a PNG or JPG image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(isAr ? 'حجم الملف يجب ألا يتجاوز 5 ميجابايت' : 'File size must not exceed 5 MB');
      return;
    }
    setUploadingAttachment(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/requests/upload-employee-attachment`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEmployeeAttachmentUrl(data.url);
      toast.success(isAr ? 'تم رفع الصورة' : 'Image uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'فشل رفع الصورة' : 'Upload failed'));
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleSubmit = async () => {
    if (isIdCard && !employeeAttachmentUrl) {
      toast.error(isAr ? 'يرجى إرفاق صورة شخصية' : 'Please attach a personal photo');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        type,
        purpose: purpose.trim(),
        country: country.trim(),
        addressee: addressee.trim(),
        employee_attachment_url: employeeAttachmentUrl || null,
      };
      if (resubmitId) {
        await axios.post(`${API}/requests/${resubmitId}/resubmit`, payload, { withCredentials: true });
        toast.success(isAr ? 'تم إعادة تقديم الطلب' : 'Request resubmitted');
      } else {
        await axios.post(`${API}/requests`, payload, { withCredentials: true });
        toast.success(isAr ? 'تم إرسال الطلب بنجاح' : 'Request submitted');
      }
      setShowForm(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'فشل الإرسال' : 'Failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm(isAr ? 'هل تريد سحب الطلب؟' : 'Cancel this request?')) return;
    try {
      await axios.delete(`${API}/requests/${id}`, { withCredentials: true });
      toast.success(isAr ? 'تم السحب' : 'Cancelled');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'فشل السحب' : 'Failed'));
    }
  };

  const handleDownload = async (req) => {
    try {
      const res = await axios.get(`${API}/requests/${req.id}/attachment`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${req.type_label_en.replace(/ /g, '_')}_${req.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'فشل التنزيل' : 'Download failed'));
    }
  };

  const requiresPurpose = ['data_amendment', 'transfer_request', 'other'].includes(type);
  const isVisa = type === 'visa_letter';
  // 'other' requests can OPTIONALLY carry an attachment (image or PDF).
  // Reuse the same uploader UX as id_card, but attachment is not required.
  const isOther = type === 'other';
  const showAttachment = isIdCard || isOther;

  return (
    <div className="min-h-screen bg-[#F2F0FA]" dir={direction} data-testid="employee-requests-page">
      {/* Header — matches the unified Leaves/Attendance employee header */}
      <header className="sticky top-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] dark:border-slate-700 py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-[#655dc6] flex items-center gap-2">
              <FileText size={28} weight="duotone" />
              {isAr ? 'طلباتي' : 'My Requests'}
            </h1>
            <p className="text-sm text-[#655dc6]/70 mt-1">
              {isAr
                ? 'تعريف راتب، شهادة خبرة، خطاب لا مانع، خطاب تأشيرة، وغيرها — يقوم قسم الموارد البشرية بتجهيز الخطاب وإرفاقه عند الموافقة.'
                : 'Salary certificates, experience letters, NOC, visa letters and more — HR will prepare and attach the letter upon approval.'}
            </p>
          </div>
          <Button onClick={openNew} className="bg-[#655dc6] hover:bg-[#544cb0] text-white gap-2" data-testid="new-request-btn">
            <Plus size={16} weight="bold" />
            {isAr ? 'طلب جديد' : 'New request'}
          </Button>
        </div>

        <Card className="border-[#C4BFF0]">
          <CardHeader>
            <CardTitle className="text-[#655dc6] text-base">
              {isAr ? `سجل الطلبات (${items.length})` : `Request history (${items.length})`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center text-[#655dc6]/70"><SpinnerGap size={28} className="animate-spin mx-auto" /></div>
            ) : items.length === 0 ? (
              <div className="py-12 text-center text-[#655dc6]/60 text-sm">
                <FileText size={48} weight="duotone" className="mx-auto mb-3 opacity-40" />
                {isAr ? 'لم تقدم أي طلب بعد. اضغط "طلب جديد" للبدء.' : 'No requests yet. Click "New request" to begin.'}
              </div>
            ) : (
              <ul className="divide-y divide-[#C4BFF0]/50">
                {items.map((r) => {
                  const meta = STATUS_META[r.status];
                  const StatusIcon = meta?.Icon || Clock;
                  const dt = fmtKsa(r.submitted_at);
                  return (
                    <li key={r.id} className="py-3 flex flex-wrap items-start justify-between gap-3" data-testid={`req-row-${r.id}`}>
                      <div className="flex-1 min-w-[240px]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-[#655dc6]">
                            {isAr ? r.type_label_ar : r.type_label_en}
                          </p>
                          {meta && (
                            <Badge className={`${meta.cls} gap-1 border`} variant="outline">
                              <StatusIcon size={12} weight="bold" />
                              {isAr ? meta.ar : meta.en}
                            </Badge>
                          )}
                        </div>
                        {r.purpose && <p className="text-xs text-[#655dc6]/70 mt-1 line-clamp-2">{r.purpose}</p>}
                        {r.country && <p className="text-[10px] text-[#655dc6]/60 mt-0.5">{isAr ? 'الدولة: ' : 'Country: '}{r.country}</p>}
                        {r.rejection_reason && (
                          <p className="text-xs text-red-700 mt-1 bg-red-50 px-2 py-1 rounded border border-red-200 inline-block">
                            {isAr ? 'سبب الرفض: ' : 'Reason: '}{r.rejection_reason}
                          </p>
                        )}
                        {r.admin_reply_text && (
                          <div
                            className="mt-2 text-xs text-emerald-900 bg-emerald-50 px-3 py-2 rounded border border-emerald-200 whitespace-pre-wrap break-words leading-relaxed"
                            data-testid={`req-admin-reply-${r.id}`}
                          >
                            <div className="font-semibold text-emerald-800 mb-0.5">
                              {isAr ? '💬 رد الإدارة' : '💬 Admin reply'}
                              {r.reply_edited_at && (
                                <span className="ms-2 font-normal text-emerald-700/70 text-[10px]">
                                  {isAr ? '(تم التحديث)' : '(updated)'}
                                </span>
                              )}
                            </div>
                            {r.admin_reply_text}
                          </div>
                        )}
                        {/* Letter metadata (doc number + expiry) */}
                        {r.doc_number && (
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px]">
                            <span className="font-mono text-[#655dc6] bg-[#EDE9FE] px-2 py-1 rounded border border-[#C4BFF0]"
                                  data-testid={`req-doc-${r.id}`}>
                              {r.doc_number}
                            </span>
                            {r.expires_at && !r.is_revoked && (
                              <span className={`px-2 py-1 rounded border ${r.is_expired
                                ? 'bg-amber-50 text-amber-800 border-amber-200'
                                : 'bg-emerald-50 text-emerald-800 border-emerald-200'}`}>
                                {isAr
                                  ? (r.is_expired ? 'انتهت الصلاحية' : `صالح حتى ${String(r.expires_at).slice(0,10)}`)
                                  : (r.is_expired ? 'Expired' : `Valid until ${String(r.expires_at).slice(0,10)}`)}
                              </span>
                            )}
                            {r.is_revoked && (
                              <span className="px-2 py-1 rounded border bg-red-50 text-red-700 border-red-200">
                                {isAr ? 'ملغى' : 'Revoked'}
                              </span>
                            )}
                          </div>
                        )}
                        <p className="text-[10px] text-[#655dc6]/50 mt-1">{dt}</p>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        {r.status === 'approved' && r.has_attachment && (
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleDownload(r)}
                            className="text-[#655dc6] gap-1"
                            data-testid={`req-download-${r.id}`}
                          >
                            <Download size={14} />
                            {isAr ? 'تنزيل' : 'Download'}
                          </Button>
                        )}
                        {r.status === 'approved' && !r.has_attachment && !r.admin_reply_text && (
                          <span className="text-[10px] text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                            {r.type === 'id_card'
                              ? (isAr ? 'قيد التجهيز — سيتم تسليمك بطاقة التعريف' : 'Being prepared — your ID card will be handed over')
                              : (isAr ? 'بانتظار رفع الخطاب من الإدارة' : 'Awaiting HR to upload the letter')}
                          </span>
                        )}
                        {r.status === 'pending' && (
                          <Button variant="ghost" size="sm" onClick={() => handleCancel(r.id)}
                                  className="text-red-600 gap-1" data-testid={`req-cancel-${r.id}`}>
                            <Trash size={14} />
                            {isAr ? 'سحب' : 'Cancel'}
                          </Button>
                        )}
                        {r.status === 'rejected' && (
                          <Button variant="ghost" size="sm" onClick={() => openResubmit(r)}
                                  className="text-[#655dc6] gap-1" data-testid={`req-resubmit-${r.id}`}>
                            <ArrowsClockwise size={14} />
                            {isAr ? 'إعادة تقديم' : 'Resubmit'}
                          </Button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </main>

      {/* New / Resubmit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent dir={direction} className="sm:max-w-[560px]" data-testid="request-form-dialog">
          <div dir={direction} style={{ direction }}>
            <DialogHeader>
              <DialogTitle className="text-[#655dc6]">
                {resubmitId
                  ? (isAr ? 'إعادة تقديم الطلب' : 'Resubmit Request')
                  : (isAr ? 'طلب جديد' : 'New Request')}
              </DialogTitle>
              <DialogDescription>
                {isIdCard
                  ? (isAr
                    ? 'يُرجى إرفاق صورة شخصية واضحة لإصدار بطاقة التعريف. بعد الاعتماد ستُطبع البطاقة وتُسلَّم لك من الإدارة.'
                    : 'Please attach a clear personal photo. After approval, HR will print and hand over the ID card.')
                  : isOther
                  ? (isAr
                    ? 'قدّم طلبك أو استفسارك لقسم شؤون الموظفين، وسيتم الرد عليك من خلال هذه الصفحة، مع إمكانية إرفاق مستند إن لزم.'
                    : 'Submit your request or inquiry to HR. You will receive a reply on this page, and you can optionally attach a document.')
                  : (isAr
                    ? 'بعد الموافقة، سيقوم قسم الموارد البشرية بتجهيز الخطاب وإرفاقه ليكون متاحاً للتنزيل من هذه الصفحة.'
                    : 'Once approved, HR will prepare and attach the letter so you can download it from this page.')}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 mt-3">
              <div>
                <Label className="text-xs text-start block mb-1">{isAr ? 'نوع الطلب' : 'Request type'}</Label>
                <Select value={type} onValueChange={setType} dir={direction} disabled={!!resubmitId}>
                  <SelectTrigger className="bg-white" data-testid="form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {types.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {isAr ? t.label_ar : t.label_en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {isVisa && (
                <div>
                  <Label className="text-xs text-start block mb-1">
                    {isAr ? 'الدولة المستهدفة *' : 'Target country *'}
                  </Label>
                  <Input value={country} onChange={(e) => setCountry(e.target.value)}
                    placeholder={isAr ? 'مثال: فرنسا' : 'e.g. France'}
                    data-testid="form-country" />
                </div>
              )}

              {showAttachment && (
                <div className="space-y-2" data-testid="form-attachment-section">
                  <Label className="text-xs text-start block mb-1">
                    {isIdCard
                      ? (isAr ? 'الصورة الشخصية *' : 'Personal photo *')
                      : (isAr ? 'مرفق (اختياري)' : 'Attachment (optional)')}
                  </Label>
                  <div className="flex items-center gap-3">
                    <input
                      id="id-card-file-input"
                      type="file"
                      accept={isIdCard ? "image/png,image/jpeg,image/jpg" : "image/png,image/jpeg,image/jpg"}
                      className="hidden"
                      onChange={(e) => handleUploadAttachment(e.target.files?.[0])}
                      data-testid="id-card-file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('id-card-file-input')?.click()}
                      disabled={uploadingAttachment}
                      className="border-[#C4BFF0] text-[#655dc6] gap-2"
                      data-testid="id-card-upload-btn"
                    >
                      {uploadingAttachment
                        ? <SpinnerGap size={14} className="animate-spin" />
                        : <UploadSimple size={14} />}
                      {employeeAttachmentUrl
                        ? (isAr ? 'تغيير الصورة' : 'Change image')
                        : (isAr ? 'رفع صورة (PNG/JPG)' : 'Upload image (PNG/JPG)')}
                    </Button>
                    {employeeAttachmentUrl && (
                      <div className="flex items-center gap-2 text-emerald-700 text-xs" data-testid="id-card-uploaded">
                        <CheckCircle size={16} weight="fill" />
                        {isAr ? 'تم رفع الصورة' : 'Image uploaded'}
                      </div>
                    )}
                  </div>
                  {employeeAttachmentUrl && (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${employeeAttachmentUrl}`}
                      alt="preview"
                      className="w-24 h-24 rounded-md object-cover border border-[#C4BFF0]"
                      data-testid="id-card-preview"
                    />
                  )}
                  {isIdCard && (
                    <div
                      className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-xs p-3 leading-relaxed"
                      data-testid="id-card-fee-notice"
                    >
                      <span className="font-semibold">
                        {isAr ? 'تنبيه: ' : 'Notice: '}
                      </span>
                      {isAr
                        ? 'قد يترتب على طباعة بطاقة التعريف مبلغ 100 ريال يُخصم من راتبك القادم.'
                        : 'A printing fee of SAR 100 may be deducted from your next salary.'}
                    </div>
                  )}
                </div>
              )}

              <div className={isIdCard ? 'hidden' : ''}>
                <Label className="text-xs text-start block mb-1">
                  {isAr ? 'تفاصيل / غرض الطلب' : 'Purpose / Details'}
                  {requiresPurpose && ' *'}
                </Label>
                <Textarea
                  rows={3}
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  placeholder={isAr
                    ? 'مثال: للسفارة الفرنسية، لفتح حساب بنكي، لتحديث رقم الجوال...'
                    : 'e.g. for the French embassy, to open a bank account, to update mobile number...'}
                  data-testid="form-purpose"
                />
              </div>

              <div className={isIdCard ? 'hidden' : ''}>
                <Label className="text-xs text-start block mb-1">
                  {isAr ? 'الجهة المرسل إليها (اختياري)' : 'Addressee (optional)'}
                </Label>
                <Input value={addressee} onChange={(e) => setAddressee(e.target.value)}
                  placeholder={isAr ? 'مثال: إلى من يهمه الأمر' : 'e.g. To Whom It May Concern'}
                  data-testid="form-addressee" />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowForm(false)} className="border-[#C4BFF0]">
                  {isAr ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button onClick={handleSubmit} disabled={submitting} className="bg-[#655dc6] hover:bg-[#544cb0] text-white gap-2" data-testid="form-submit">
                  {submitting ? <SpinnerGap size={16} className="animate-spin" /> : <Plus size={14} />}
                  {resubmitId ? (isAr ? 'إعادة تقديم' : 'Resubmit') : (isAr ? 'إرسال' : 'Submit')}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
